import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { eventBookingAPI } from '../services/api';

const EventBookingDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [bookingData, setBookingData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBooking = async () => {
            try {
                setLoading(true);
                const bookingId = location.state?.bookingId || location.pathname.split('/').pop();
                if (location.state?.bookingData) {
                    setBookingData(location.state.bookingData);
                    setLoading(false);
                } else if (bookingId) {
                    const response = await eventBookingAPI.getEventBookingById(bookingId);
                    if (response.booking) {
                        setBookingData(response.booking);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch event booking:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBooking();
    }, [location]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#FF5E4C] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!bookingData) {
        return (
            <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-[16px] text-gray-600 mb-4">Event booking not found</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2 bg-[#FF5E4C] text-white rounded-[12px]"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';

        // If already in AM/PM format, return as is
        if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
            return timeString;
        }

        // Try to parse as time string (HH:MM or HH:MM:SS)
        const timeMatch = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (timeMatch) {
            let hours = parseInt(timeMatch[1], 10);
            const minutes = timeMatch[2];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // the hour '0' should be '12'
            return `${hours}:${minutes} ${ampm}`;
        }

        // Fallback: return original string
        return timeString;
    };

    const formatDateTime = (dateString, timeString) => {
        const date = formatDate(dateString);
        const formattedTime = formatTime(timeString);
        return `${date} ${formattedTime}`.trim();
    };

    const getStatusBadge = (status, paymentStatus) => {
        // Check payment status from payments array or direct paymentStatus field
        const effectivePaymentStatus = paymentStatus ||
            (bookingData.payments && bookingData.payments.length > 0
                ? bookingData.payments[bookingData.payments.length - 1].status
                : null);

        if (status === 'CONFIRMED' && (effectivePaymentStatus === 'SUCCEEDED' || effectivePaymentStatus === 'SUCCESS')) {
            return { text: 'Completed', bgColor: 'bg-[#10B981]', textColor: 'text-white' };
        } else if (status === 'PENDING' || effectivePaymentStatus === 'PENDING') {
            return { text: 'In Progress', bgColor: 'bg-[#8B5CF6]', textColor: 'text-white' };
        } else {
            return { text: 'In Progress', bgColor: 'bg-[#8B5CF6]', textColor: 'text-white' };
        }
    };

    const statusBadge = getStatusBadge(bookingData.status, bookingData.paymentStatus);

    // Get ticket details from lineItems
    const lineItems = bookingData.lineItems || [];
    const totalTickets = lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

    // Calculate pricing
    const ticketSubtotal = lineItems.reduce((sum, item) => {
        const itemTotal = (item.unitPrice?.discountedPrice || item.unitPrice?.amount || item.price || 0) * (item.quantity || 0);
        return sum + itemTotal;
    }, 0);

    const taxes = lineItems.reduce((sum, item) => {
        const itemTaxes = (item.unitPrice?.taxes || item.taxes || 0) * (item.quantity || 0);
        return sum + itemTaxes;
    }, 0) || bookingData.pricingSummary?.taxes || 0;

    const discount = bookingData.pricingSummary?.discount || 0;
    const total = bookingData.pricingSummary?.grandTotal || bookingData.totalAmount || ticketSubtotal - discount;

    // Get guest and pet counts from attendees or tickets
    const attendees = bookingData.attendees || [];
    const totalGuests = attendees.length > 0 ? attendees.length : (bookingData.guestCount || totalTickets || 1);
    const totalPets = attendees.filter(attendee => attendee.petName).length || bookingData.petCount || 0;

    // Get venue location for event info card (can be truncated)
    const city = bookingData.event?.city || '';
    const state = bookingData.event?.state || '';
    const eventLocationText = `${city}${state ? `, ${state}` : ''}`.trim() ||
        bookingData.event?.address ||
        'Location not available';

    // Get venue location for Event Details section (full venue address)
    const venue = bookingData.show?.venue || bookingData.event?.venue || '';
    const venueLocationText = venue && city
        ? `${venue}, ${city}${state ? `, ${state}` : ''}`.trim()
        : venue
            ? venue
            : `${city}${state ? `, ${state}` : ''}`.trim() ||
            bookingData.event?.address ||
            'Location not available';

    return (
        <div className="min-h-screen bg-[#FAF7F2]">
            <div className="bg-[#FAF7F2] md:px-[120px] p-[20px] md:pt-[60px] md:pb-[20px] gap-[16px] flex items-center justify-start">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="hover:bg-gray-100 rounded-full p-1 shrink-0 flex items-center justify-center"
                    aria-label="Go back"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <g clip-path="url(#clip0_3371_46)">
                            <path d="M5 12H19" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M5 12L11 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M5 12L11 6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_3371_46">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </button>
                <div className="flex items-center gap-[6px]">
                    <span className="text-[18px] font-normal text-black uppercase leading-none">
                        # {bookingData.bookingId || 'N/A'}
                    </span>
                    <span
                        className={`px-[12px] py-[4px] rounded-full text-[12px] font-medium ${statusBadge.bgColor} ${statusBadge.textColor}`}
                    >
                        {statusBadge.text}
                    </span>
                </div>
            </div>

            <div className="md:px-[120px] p-[20px] md:py-[20px] pb-10 w-full space-y-[8px]">
                {/* Event Information Card */}
                <div className="bg-white rounded-[16px] p-[14px] md:p-[24px] shadow-sm ">
                    <div className="flex gap-[8px]">
                        <img
                            src={bookingData.event?.images?.[0] || '/assets/logo.svg'}
                            alt={bookingData.event?.title || 'Event'}
                            className="w-[100px] h-[100px] rounded-[12px] object-cover"
                        />
                        <div className="flex-1 py-[12px]">
                            <h2 className="text-[18px] font-medium text-black mb-[8px] leading-none">
                                {bookingData.event?.title || 'Event'}
                            </h2>
                            <p className="text-[12px] text-[#bebebe] leading-none">
                                {eventLocationText}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Event Details Card */}
                <div className="bg-white rounded-[14px] p-[20px] md:p-[28px] shadow-sm ">
                    <h3 className="text-[18px] font-medium text-black leading-none">Event Details</h3>

                    {/* Divider */}
                    <div className='flex my-[20px] items-center justify-center'>
                        <svg width="100%" height="1px">
                            <line
                                x1="0" y1="1" x2="100%" y2="1"
                                stroke="#f0f0f0"
                                strokeWidth="2"
                                strokeDasharray="10 4"
                            />
                        </svg>
                    </div>

                    <div className="space-y-[16px]">
                        {/* Slot */}
                        {bookingData.show && (
                            <div>
                                <div className="flex items-center gap-[4px] mb-[4px]">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <g clipPath="url(#clip0_2846_6297)">
                                            <path
                                                d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z"
                                                stroke="black"
                                                strokeWidth="0.583333"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M9.33203 1.75V4.08333"
                                                stroke="black"
                                                strokeWidth="0.583333"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M4.66797 1.75V4.08333"
                                                stroke="black"
                                                strokeWidth="0.583333"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M2.33203 6.41675H11.6654"
                                                stroke="black"
                                                strokeWidth="0.583333"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2846_6297">
                                                <rect width="14" height="14" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <span className="text-[12px] text-[#999999] leading-none">Slot</span>
                                </div>
                                <p className="text-[18px] font-normal text-black leading-none">
                                    {formatDate(bookingData.show.date)} <span className='text-[16px] font-medium text-[#636363] ml-[6px]'>{formatTime(bookingData.show.time)}</span>
                                </p>
                            </div>
                        )}

                        {/* Venue */}
                        <div>
                            <div className="flex items-center gap-[4px] mb-[4px]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <g clip-path="url(#clip0_3371_376)">
                                        <path d="M5.25 6.41669C5.25 6.88082 5.43437 7.32594 5.76256 7.65412C6.09075 7.98231 6.53587 8.16669 7 8.16669C7.46413 8.16669 7.90925 7.98231 8.23744 7.65412C8.56563 7.32594 8.75 6.88082 8.75 6.41669C8.75 5.95256 8.56563 5.50744 8.23744 5.17925C7.90925 4.85106 7.46413 4.66669 7 4.66669C6.53587 4.66669 6.09075 4.85106 5.76256 5.17925C5.43437 5.50744 5.25 5.95256 5.25 6.41669Z" stroke="black" stroke-width="0.875" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M10.3 9.7166L7.82488 12.1917C7.60612 12.4102 7.30955 12.533 7.00033 12.533C6.69112 12.533 6.39455 12.4102 6.17579 12.1917L3.70013 9.7166C3.04751 9.06394 2.60307 8.23242 2.42303 7.32718C2.24298 6.42194 2.33541 5.48365 2.68863 4.63094C3.04184 3.77823 3.63998 3.04941 4.40741 2.53664C5.17483 2.02387 6.07707 1.75018 7.00004 1.75018C7.92301 1.75018 8.82525 2.02387 9.59268 2.53664C10.3601 3.04941 10.9582 3.77823 11.3115 4.63094C11.6647 5.48365 11.7571 6.42194 11.5771 7.32718C11.397 8.23242 10.9526 9.06394 10.3 9.7166Z" stroke="black" stroke-width="0.875" stroke-linecap="round" stroke-linejoin="round" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3371_376">
                                            <rect width="14" height="14" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span className="text-[12px] text-[#999999]">Venue</span>
                            </div>
                            <p className="text-[18px] font-normal text-black leading-none">
                                {venueLocationText}
                            </p>
                        </div>

                        {/* Seats */}
                        <div>
                            <div className="flex items-center gap-[4px] mb-[4px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <g clip-path="url(#clip0_3371_386)">
    <path d="M2.91663 4.08333C2.91663 4.70217 3.16246 5.29566 3.60004 5.73325C4.03763 6.17083 4.63112 6.41667 5.24996 6.41667C5.8688 6.41667 6.46229 6.17083 6.89988 5.73325C7.33746 5.29566 7.58329 4.70217 7.58329 4.08333C7.58329 3.46449 7.33746 2.871 6.89988 2.43342C6.46229 1.99583 5.8688 1.75 5.24996 1.75C4.63112 1.75 4.03763 1.99583 3.60004 2.43342C3.16246 2.871 2.91663 3.46449 2.91663 4.08333Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M9.33337 1.82581C9.83528 1.95431 10.2801 2.24621 10.5978 2.65549C10.9155 3.06476 11.0879 3.56812 11.0879 4.08622C11.0879 4.60432 10.9155 5.10769 10.5978 5.51696C10.2801 5.92623 9.83528 6.21813 9.33337 6.34664" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12.25 12.25V11.0834C12.247 10.5684 12.0738 10.0688 11.7572 9.66259C11.4407 9.25636 10.9986 8.96627 10.5 8.83752" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_3371_386">
      <rect width="14" height="14" fill="white"/>
    </clipPath>
  </defs>
</svg>
                                <span className="text-[12px] text-[#999999] leading-none">Seats</span>
                            </div>  
                            <p className="text-[18px] font-normal text-black leading-none">
                                {totalGuests} guest{totalGuests !== 1 ? 's' : ''}{totalPets > 0 ? ` , ${totalPets} pet${totalPets !== 1 ? 's' : ''}` : ''}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Fare Summary Card */}
                <div className="bg-white rounded-[14px] p-[20px] md:p-[28px] shadow-sm">
                    <h3 className="text-[18px] font-medium text-black leading-none">Fare Summary</h3>

                    {/* Divider */}
                    <div className='flex my-[20px] items-center justify-center'>
                        <svg width="100%" height="1px">
                            <line
                                x1="0" y1="1" x2="100%" y2="1"
                                stroke="#f0f0f0"
                                strokeWidth="2"
                                strokeDasharray="10 4"
                            />
                        </svg>
                    </div>

                    <div className="space-y-[12px]">
                        {/* Ticket Types */}
                        {lineItems.length > 0 ? (
                            lineItems.map((item, index) => (
                                <div key={index} className="flex justify-between items-center leading-none">
                                    <span className="text-[16px] text-[#7e7e7e]">
                                        {item.title || item.ticketTypeName || `Ticket Type ${index + 1}`} x {item.quantity || 1}
                                    </span>
                                    <span className="text-[16px] font-medium  text-[#636363]">
                                        ₹{((item.unitPrice?.discountedPrice || item.unitPrice?.amount || item.price || 0) * (item.quantity || 1)).toLocaleString('en-IN')}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="flex justify-between items-center leading-none">
                                <span className="text-[16px] text-[#7e7e7e]">
                                    Ticket Type 1 x 1
                                </span>
                                <span className="text-[16px] font-medium  text-[#636363]">
                                    ₹{ticketSubtotal.toLocaleString('en-IN')}
                                </span>
                            </div>
                        )}

                        {/* Included Taxes & Charges */}
                        <div className="flex justify-between items-center leading-none">
                            <span className="text-[16px] text-[#7e7e7e]">Included Taxes & Charges</span>
                            <span className="text-[16px] font-medium  text-[#636363]">
                                ₹{taxes.toLocaleString('en-IN')}
                            </span>
                        </div>

                        {/* Discounts */}
                        {discount > 0 && (
                            <div className="flex justify-between items-center leading-none">
                                <span className="text-[16px] text-[#7e7e7e]">Discounts</span>
                                <span className="text-[16px] font-medium text-[#10B981]">
                                    -₹{discount.toLocaleString('en-IN')}
                                </span>
                            </div>
                        )}

                        {/* Total Payable */}
                        <div className="leading-none flex justify-between items-center">
                            <span className="text-[16px] text-[#7e7e7e]">Total Payable</span>
                            <span className="text-[20px] font-medium text-black">
                                ₹{total.toLocaleString('en-IN')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventBookingDetail;

